import React, { useCallback, useEffect, useMemo, useState } from 'react';

import useToggle from '@lyearn/core/hooks/useToggle';
import { useTranslation } from '@lyearn/i18n';
import { SparkleOutline } from '@lyearn/icons';
import {
  getInputPlaceholderPlugin,
  getSelectionPlugins,
  hideSelectionMenu,
  MarkType,
} from '@lyearn/lygo-blocks';
import { EditorView, NewEditor } from '@lyearn/lygo-core';
import { cn } from '@lyearn/style';
import { BaseButton, Box, ClickAwayListener, Typography } from '@lyearn/ui';
import useAnalytics from '@/components/Analytics/hooks/useAnalytics';
import { PlainEditorProps } from '@/components/Editor/PlainEditor/types';
import { FeatureFlags, useFeatureFlags } from '@/helper/FeatureFlags';
import { quizEditorExtensions } from '@/lygo/QuizEditor';

import QuestionSuggestionContainer from './components/QuestionSuggestionContainer';
import { aiMenuClassName, aiMenuIconFill, aiMenuText } from './styles';
import { getCustomMarkType } from './utils';

function RichTextEditor(props: PlainEditorProps & { showRephraseOption?: boolean }) {
  const {
    className,
    variant = 'body-short-01',
    value,
    onChangeText,
    placeholder,
    showRephraseOption,
    disabled,
  } = props;
  const { t } = useTranslation();
  const [showQuestionSuggestion, { toggle: toggleShowQuestionSuggestion }] = useToggle();
  const [editorView, setEditorView] = useState<EditorView | undefined>();
  const { [FeatureFlags.EnableLyearnAI]: enableLyearnAI } = useFeatureFlags();
  const { track } = useAnalytics();

  const RephraseComponent = useMemo<MarkType['CustomComponent']>(() => {
    return () => {
      return (
        <BaseButton
          className={cn(aiMenuClassName, 'mx-2 h-28 rounded-6 ps-6 pe-10')}
          onClick={() => {
            track('Rephrase question');
            toggleShowQuestionSuggestion();
          }}>
          <SparkleOutline className={cn('h-16 w-16 me-6', aiMenuIconFill)} />
          <Typography className={aiMenuText} variant="label-02">
            {t('QuizCreator:AI.Rephrase')}
          </Typography>
        </BaseButton>
      );
    };
  }, [t, toggleShowQuestionSuggestion, track]);

  const isAIOptionEnabled = enableLyearnAI && showRephraseOption;

  useEffect(() => {
    if (editorView && showQuestionSuggestion) {
      hideSelectionMenu(editorView.state, editorView.dispatch);
    }
  }, [editorView, showQuestionSuggestion]);

  const onClickAway = useCallback(() => {
    if (!editorView || disabled) {
      return;
    }

    // hide the formatting toolbar if user clicks outside the editor area
    hideSelectionMenu(editorView.state, editorView.dispatch);
  }, [disabled, editorView]);

  return (
    <>
      <ClickAwayListener onClickAway={onClickAway}>
        <Box
          className={cn('flex-1 overflow-hidden', variant, className, {
            'pointer-events-none': showQuestionSuggestion,
          })}>
          <NewEditor
            extensions={quizEditorExtensions}
            plugins={[
              () => getInputPlaceholderPlugin(placeholder ?? t('QuizCreator:QuestionPlaceholder')),
              getSelectionPlugins(
                isAIOptionEnabled ? getCustomMarkType(t, RephraseComponent) : undefined,
              ),
            ]}
            readOnly={disabled}
            value={value ?? undefined}
            onChange={(_, htmlValue) => onChangeText(htmlValue)}
            onCreateEditorView={setEditorView}
          />
        </Box>
      </ClickAwayListener>
      {editorView && showQuestionSuggestion ? (
        <QuestionSuggestionContainer
          editorView={editorView}
          onClose={toggleShowQuestionSuggestion}
        />
      ) : null}
    </>
  );
}

export default RichTextEditor;
